// Reads the one editable content layer. Super admins edit these rows at
// /admin/content, and every member facing fulfillment string reads from here.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ContentBlock {
  id: string;
  slug: string;
  kind: string;
  title: string;
  subject: string | null;
  preheader: string | null;
  from_name: string | null;
  reply_to: string | null;
  body_markdown: string;
  member_facing: boolean;
  version: number;
  updated_at: string;
}

/** What an inbox shows when a template leaves the identity fields blank. */
export const HOUSE_FROM_NAME = "Wealthy Steward";
export const HOUSE_FROM_EMAIL = "noreply@wlthystwrd.com";


export const BLOCK_KINDS = ["email", "system_email", "tracking", "wait", "gate", "qr", "copy"] as const;
export type BlockKind = (typeof BLOCK_KINDS)[number];

export const KIND_LABEL: Record<string, string> = {
  email: "Member emails",
  system_email: "System emails",
  tracking: "Tracking page",
  wait: "While you wait",
  gate: "Packet gate",
  qr: "Code landing",
  copy: "Other wording",
};

/** True when a block's body is raw HTML rather than markdown. */
export function isHtmlBlock(kind: string): boolean {
  return kind === "system_email";
}

/** Fills {{name}} style spots with real values, leaving nothing empty on screen. */
export function fillBlockTokens(text: string, vars: Record<string, unknown>): string {
  return (text || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key: string) => {
    const value = vars[key];
    if (value === undefined || value === null || value === "") return "";
    return String(value);
  });
}

/** Loads blocks of one kind, keyed by slug. */
export function useContentBlocks(kind: BlockKind | BlockKind[]) {
  const kinds = Array.isArray(kind) ? kind : [kind];
  const key = kinds.join(",");
  const [blocks, setBlocks] = useState<Record<string, ContentBlock>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await (supabase as any)
      .from("content_blocks")
      .select("id, slug, kind, title, subject, preheader, from_name, reply_to, body_markdown, member_facing, version, updated_at")
      .in("kind", key.split(","))
      .order("slug", { ascending: true });
    const map: Record<string, ContentBlock> = {};
    for (const row of (data || []) as ContentBlock[]) map[row.slug] = row;
    setBlocks(map);
    setLoading(false);
  }, [key]);

  useEffect(() => {
    void load();
  }, [load]);

  return { blocks, loading, reload: load };
}
